import { State as DataState, SortDescriptor } from '@progress/kendo-data-query';
import {
  GridColumn,
  GridColumnMenuWrapperProps,
  GridColumnProps,
  GridHeaderCellProps,
  Grid as KendoGrid,
  GridProps as KendoGridProps,
} from '@progress/kendo-react-grid';
import { PropsWithChildren } from 'react';
import { config } from '../global/config';
import { GridPager } from './GridPager';
import Icons from './Icons';

interface GridCustomProps {
  columns: GridColumnProps[];
  hideHeaderRow?: boolean;
}

type GridProps = GridCustomProps & KendoGridProps;

export default function Grid(props: GridProps) {
  const { columns, hideHeaderRow } = props;

  return (
    <KendoGrid {...props}>
      {columns.map((c, idx) => (
        <GridColumn
          key={idx}
          {...c}
          headerCell={DefaultHeader}
          className={c.className ?? ''}
          headerClassName={`${c.headerClassName} ${
            hideHeaderRow ? 'display-none' : ''
          }`}
        />
      ))}
    </KendoGrid>
  );
}

Grid.defaultProps = {
  hideHeaderRow: false,
  pager: GridPager,
};

const getSortingIcon = (
  wrapperProps: GridColumnMenuWrapperProps,
  title?: string,
  field?: string,
) => {
  const hasTitle = title;
  const isSortable = wrapperProps.sortable;
  const sort: SortDescriptor | undefined = wrapperProps.sort?.length
    ? wrapperProps.sort.find(s => {
        return s.field === field;
      })
    : undefined;
  const isAsc = sort && sort.dir === 'asc';
  const isDesc = sort && sort.dir === 'desc';
  if (hasTitle && isSortable && !(isAsc || isDesc))
    return (
      <span>
        <Icons.SortDefault />
      </span>
    );
  else if (isSortable && isAsc)
    return (
      <span>
        <Icons.SortAsc />{' '}
      </span>
    );
  else if (isSortable && isDesc)
    return (
      <span>
        <Icons.SortDesc />{' '}
      </span>
    );
  else if (!isSortable && isAsc)
    return (
      <span>
        <Icons.ArrowUp />
      </span>
    );
  else if (!isSortable && isDesc)
    return (
      <span>
        <Icons.SortSingle />
      </span>
    );
};

const DefaultHeader = (props: GridHeaderCellProps) => {
  const isSortable = props.columnMenuWrapperProps?.sortable;

  return (
    <span
      className={isSortable ? 'k-link' : ''}
      onClick={isSortable ? props.onClick : undefined}
    >
      {props.title}
      <span className="k-sort-icon">
        {getSortingIcon(props.columnMenuWrapperProps, props.title, props.field)}
      </span>
    </span>
  );
};

export const GridCellTd = (
  props: PropsWithChildren & { className?: string },
) => (
  <td
    className={props.className ? `k-table-td ${props.className}` : 'k-table-td'}
  >
    <span>{props.children}</span>
  </td>
);

export const getSkip = (
  previousDataState: DataState,
  newDataState: DataState,
): number => {
  return (newDataState.take || config.grid.defaultPageSize) !==
    (previousDataState.take || config.grid.defaultPageSize)
    ? 0
    : newDataState.skip ?? 0;
};
